import React, { useState } from "react";
import { GraduationCap, CheckCircle2, XCircle, Award, RotateCcw } from "lucide-react";
import { usePoll } from "@/hooks/usePoll";
import { endpoints } from "@/lib/api";
import { Panel, PageHeader, Loading, StatusBadge } from "@/components/common";

export default function OperatorTraining() {
  const { data } = usePoll(endpoints.trainingScenarios, 0);
  const { data: lb, refresh: refreshLb } = usePoll(endpoints.trainingLeaderboard, 0);
  const [name, setName] = useState("");
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  if (!data) return <Loading />;
  const scenarios = data.scenarios;

  const submit = async () => {
    setBusy(true);
    try {
      const res = await endpoints.trainingSubmit(name, answers);
      setResult(res);
      refreshLb();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally { setBusy(false); }
  };

  const reset = () => { setAnswers({}); setResult(null); };
  const detail = (id) => result?.details?.find((d) => d.id === id);

  return (
    <div data-testid="operator-training-page">
      <PageHeader icon={GraduationCap} title="Operator Training & Certification"
        subtitle="A safe training instance on the Digital Plant — diagnose failures, get scored, earn certification." />

      {result && (
        <Panel testid="training-result" className="mb-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center ${result.passed ? "bg-emerald-50" : "bg-red-50"}`}>
                <span className={`text-xl font-bold font-mono ${result.passed ? "text-emerald-600" : "text-red-600"}`}>{result.percent}%</span>
              </div>
              <div>
                <div className="text-lg font-semibold text-slate-900">{result.passed ? "Passed — Certified!" : "Not passed yet"}</div>
                <div className="text-sm text-slate-500">{result.earned} / {result.total} points · {result.name}</div>
              </div>
            </div>
            {result.certificate && (
              <div className="flex items-center gap-3 bg-slate-900 text-white rounded-md px-4 py-3" data-testid="certificate">
                <Award size={28} className="text-amber-400" />
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-300">{result.certificate.level} · Ergoniq Operator</div>
                  <div className="font-mono font-semibold">{result.certificate.id}</div>
                  <div className="text-[11px] text-slate-400">{new Date(result.certificate.issued_at).toLocaleDateString("en-IN")}</div>
                </div>
              </div>
            )}
            <button onClick={reset} data-testid="retake-btn" className="flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900">
              <RotateCcw size={14} /> Retake
            </button>
          </div>
        </Panel>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 space-y-4">
          {!result && (
            <Panel testid="trainee-name">
              <label className="block">
                <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Trainee Name</span>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="trainee-name-input"
                  className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300" />
              </label>
            </Panel>
          )}

          {scenarios.map((sc, i) => {
            const d = detail(sc.id);
            return (
              <Panel key={sc.id} testid={`scenario-${sc.id}`}
                title={`${i + 1}. ${sc.title}`} subtitle={`${sc.twin} · ${sc.difficulty} · ${sc.points} pts`}
                right={d && <StatusBadge status={d.correct ? "Normal" : "Critical"}>{d.correct ? "Correct" : "Incorrect"}</StatusBadge>}>
                <p className="text-sm text-slate-600 mb-3">{sc.brief}</p>
                <p className="text-sm font-medium text-slate-900 mb-2">{sc.question}</p>
                <div className="space-y-2">
                  {sc.options.map((opt, oi) => {
                    const chosen = answers[sc.id] === oi;
                    const isAnswer = d && d.answer === oi;
                    const isWrongChoice = d && d.chosen === oi && !d.correct;
                    return (
                      <button key={oi} disabled={!!result} data-testid={`option-${sc.id}-${oi}`}
                        onClick={() => setAnswers((a) => ({ ...a, [sc.id]: oi }))}
                        className={`w-full text-left text-sm px-3 py-2 rounded-md border transition-colors flex items-center gap-2 ${
                          isAnswer ? "border-emerald-500 bg-emerald-50" :
                          isWrongChoice ? "border-red-400 bg-red-50" :
                          chosen ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-300"
                        }`}>
                        {isAnswer && <CheckCircle2 size={15} className="text-emerald-600 shrink-0" />}
                        {isWrongChoice && <XCircle size={15} className="text-red-500 shrink-0" />}
                        <span>{opt}</span>
                      </button>
                    );
                  })}
                </div>
                {d && <div className="mt-3 text-xs text-slate-600 bg-slate-50 border border-slate-100 rounded p-2.5"><b>Why:</b> {d.explanation}</div>}
              </Panel>
            );
          })}

          {!result && (
            <button onClick={submit} disabled={busy || Object.keys(answers).length < scenarios.length} data-testid="submit-assessment-btn"
              className="w-full bg-slate-900 text-white py-3 rounded-md text-sm font-semibold hover:bg-slate-700 disabled:opacity-50 transition-colors">
              {busy ? "Scoring…" : Object.keys(answers).length < scenarios.length ? `Answer all ${scenarios.length} scenarios` : "Submit Assessment"}
            </button>
          )}
        </div>

        <Panel testid="leaderboard" title="Certification Leaderboard" subtitle="Top certified operators">
          {(lb?.leaderboard || []).length === 0 ? (
            <div className="text-sm text-slate-400 py-6 text-center">No certifications yet — be the first!</div>
          ) : (
            <div className="space-y-2">
              {lb.leaderboard.map((r, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <span className="w-5 text-slate-400 font-mono text-xs">{i + 1}</span>
                  <span className="flex-1 text-slate-700">{r.name}</span>
                  <span className="font-mono font-semibold text-emerald-600">{r.percent}%</span>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
